import { Alert } from 'antd'
import {
  unwrapFieldValue,
  wrapFieldValue,
} from 'features/libra/ontology/ontologyFieldDefaults'
import { NiiEligibilitySelect } from 'features/libra/stepComponents/niiReport/EligibilityTag'
import { EditableStringList } from 'features/libra/stepComponents/niiReport/NiiFieldComponents'
import { getComputedEligibility } from 'features/libra/stepComponents/niiReport/niiReportApi'
import { useNiiReportData } from 'features/libra/stepComponents/niiReport/NiiReportDataContext'
import {
  DescriptionItem,
  OptionalDescriptions,
} from 'features/libra/stepComponents/niiReport/OptionalDescriptions'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import {
  ELIGIBILITY_LABELS,
  ELIGIBILITY_STATUS,
  type EligibilityStatus,
} from 'features/libra/types/eligibility'
import React, { FC, useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { StyledCard } from './cardsStyles'

const WarningBanner = styled(Alert)`
  margin-bottom: 1rem;
`

const ConclusionCautionText =
  'This NII inspection report is only valid on the next scheduled inspection assuming there are no changes to process conditions and/or expected degradation. Any changes to expected degradation will require an updated NII assessment.'

interface NiiConclusionCardProps {
  eligibilityField: string
  eligibilityComputedField: string
  recommendationsField: string
  readonly?: boolean
}

export const RawNiiConclusionCard: FC<NiiConclusionCardProps> = ({
  eligibilityField,
  eligibilityComputedField,
  recommendationsField,
  readonly,
}) => {
  const { localValues, setLocalFieldValue, isLoading } = useNiiReportData()
  // computedEligibility is written by the Screening step, not locally managed —
  // read it from the server state directly.
  const { fieldValues: ticketFieldValues } = useTicketAccess()

  const computedEligibility = useMemo(
    () => getComputedEligibility(ticketFieldValues, eligibilityComputedField),
    [ticketFieldValues, eligibilityComputedField]
  )

  const eligibility = unwrapFieldValue<EligibilityStatus>(
    localValues[eligibilityField]
  )

  const recommendations = useMemo(
    () =>
      unwrapFieldValue<ReadonlyArray<string>>(
        localValues[recommendationsField]
      ) ?? [],
    [localValues, recommendationsField]
  )

  const handleSetEligibility = useCallback(
    (value: EligibilityStatus) => {
      setLocalFieldValue(eligibilityField, wrapFieldValue(value))
    },
    [setLocalFieldValue, eligibilityField]
  )

  const handleSetRecommendations = useCallback(
    (value: ReadonlyArray<string>) => {
      setLocalFieldValue(recommendationsField, wrapFieldValue([...value]))
    },
    [setLocalFieldValue, recommendationsField]
  )

  const computedEligibilityLabel = useMemo(() => {
    if (computedEligibility === ELIGIBILITY_STATUS.UNCLEAR) return undefined
    if (computedEligibility === eligibility) return undefined
    return ELIGIBILITY_LABELS[computedEligibility]
  }, [computedEligibility, eligibility])

  const recommendationsNode = useMemo(() => {
    if (!readonly) {
      return (
        <EditableStringList
          key='recommendations'
          items={recommendations}
          setItems={handleSetRecommendations}
          notes
        />
      )
    }

    if (recommendations.length === 0) {
      return '-'
    }

    return recommendations.map((item, index) => (
      <div key={`${item}-${index}`}>{item}</div>
    ))
  }, [readonly, recommendations, handleSetRecommendations])

  const items = useMemo<ReadonlyArray<DescriptionItem>>(
    () => [
      [
        'Eligibility',
        <NiiEligibilitySelect
          key='eligibility'
          eligibility={eligibility}
          setEligibility={readonly ? undefined : handleSetEligibility}
        />,
      ] as const,
      ['Recommendations', recommendationsNode] as const,
    ],
    [eligibility, recommendationsNode, handleSetEligibility, readonly]
  )

  return (
    <StyledCard title='Conclusion'>
      <WarningBanner title={ConclusionCautionText} type='warning' showIcon />
      {computedEligibilityLabel !== undefined && (
        <WarningBanner
          type='info'
          showIcon
          message={
            <>
              {'Screening result: '}
              <span
                style={{
                  color:
                    computedEligibility === ELIGIBILITY_STATUS.ELIGIBLE
                      ? '#52c41a'
                      : '#ff4d4f',
                  fontWeight: 600,
                }}>
                {computedEligibilityLabel}
              </span>
            </>
          }
        />
      )}
      <OptionalDescriptions isLoading={isLoading} items={items} />
    </StyledCard>
  )
}

export const NiiConclusionCard = React.memo(RawNiiConclusionCard)
